import { VisualAssessmentRating } from '@/components/VisualAssessmentBox/VisualAssessmentBox.types';
import { getTailwindColorHex } from './tailwind-colors';
import { APP_COLORS } from '@/config/colors';

export enum Verdict {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
  NEUTRAL = 'neutral',
  INFO = 'info',
  EMPTY = 'empty',
}

export const getVerdictStyles = (
  verdict: Verdict,
  version: 'v1' | 'v2' = 'v1',
) => {
  if (version === 'v1') {
    switch (verdict) {
      case Verdict.POSITIVE:
        return 'bg-green-700 text-white';
      case Verdict.NEGATIVE:
        return 'bg-red-600 text-white';
      case Verdict.NEUTRAL:
        return 'bg-yellow text-white';
      case Verdict.INFO:
        return 'bg-blue-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  } else {
    switch (verdict) {
      case Verdict.POSITIVE:
        return 'text-green-700 bg-green-200';
      case Verdict.NEGATIVE:
        return 'bg-red-200 text-red-600';
      case Verdict.NEUTRAL:
        return 'bg-yellow text-[#BCA010]';
      case Verdict.INFO:
        return 'bg-blueLightest text-primary';
      default:
        return 'bg-gray-400 text-gray-600';
    }
  }
};

export const getVerdictColor = (verdict: Verdict) => {
  const verdictStyles = getVerdictStyles(verdict);
  return getTailwindColorHex(verdictStyles);
};

export const getRatingStyle = (rating: number) => {
  switch (rating) {
    case 0:
      return 'bg-red-800';
    case 1:
      return 'bg-red-800';
    case 2:
      return 'bg-orangeBright';
    case 3:
      return 'bg-yellowDark';
    case 4:
      return 'bg-green-500';
    case 5:
      return 'bg-green-700';
    default:
      return 'bg-gray-200';
  }
};

export const getRatingColor = (rating: number) => {
  const ratingStyles = getRatingStyle(rating);
  return getTailwindColorHex(ratingStyles);
};

export const getVerdictText = (verdict: Verdict) => {
  switch (verdict) {
    case Verdict.POSITIVE:
      return 'Pozytywna';
    case Verdict.NEGATIVE:
      return 'Negatywna';
    case Verdict.NEUTRAL:
      return 'Neutralna';
    case Verdict.EMPTY:
      return 'Brak';
    default:
      return 'Brak';
  }
};

export const getVerdict = (rating: number | Verdict) => {
  if (typeof rating === 'number') {
    if (rating >= 4) return Verdict.POSITIVE;
    if (rating === 3.5) return Verdict.NEUTRAL;
    if (rating === 3) return Verdict.NEUTRAL;
    if (rating < 3) return Verdict.NEGATIVE;
  } else if (rating === Verdict.POSITIVE) {
    return Verdict.POSITIVE;
  } else if (rating === Verdict.NEGATIVE) {
    return Verdict.NEGATIVE;
  } else if (rating === Verdict.NEUTRAL) {
    return Verdict.NEUTRAL;
  } else if (rating === Verdict.INFO) {
    return Verdict.INFO;
  }

  return Verdict.EMPTY;
};

export const getRatingMessage = (rating: number): string => {
  switch (rating) {
    case 5:
      return 'Brak zastrzeżeń';
    case 4:
      return 'Drobne zastrzeżenia';
    case 3:
      return 'Umiarkowane zastrzeżenia';
    case 2:
      return 'Poważne zastrzeżenia';
    case 1:
      return 'Krytyczne zastrzeżenia';
    default:
      return 'Brak zastrzeżeń';
  }
};

export enum OilColor {
  GREEN = 'green',
  BLUE = 'blue',
  RED = 'red',
  PINK = 'pink',
  ORANGE = 'orange',
  YELLOW = 'yellow',
  MILKY = 'milky',
  GOLD = 'gold',
  WHITE = 'white',
  BROWN = 'brown',
  BLACK = 'black',
}

export const getOilColorStyles = (color: OilColor) => {
  switch (color) {
    case OilColor.GREEN:
      return 'bg-oil-green text-white';
    case OilColor.BLUE:
      return 'bg-oil-blue text-white';
    case OilColor.RED:
      return 'bg-oil-red text-white';
    case OilColor.PINK:
      return 'bg-oil-pink text-white';
    case OilColor.ORANGE:
      return 'bg-oil-orange text-white';
    case OilColor.YELLOW:
      return 'bg-oil-yellow text-black';
    case OilColor.MILKY:
      return 'bg-oil-milky text-black border border-gray-300';
    case OilColor.GOLD:
      return 'bg-oil-gold text-black';
    case OilColor.WHITE:
      return 'bg-oil-white text-black border border-gray-300';
    case OilColor.BROWN:
      return 'bg-oil-brown text-white';
    case OilColor.BLACK:
      return 'bg-oil-black text-white';
    default:
      return 'bg-oil-green text-white';
  }
};

export const getOilColorText = (color: OilColor) => {
  switch (color) {
    case OilColor.GREEN:
      return 'Zielony';
    case OilColor.BLUE:
      return 'Niebieski';
    case OilColor.RED:
      return 'Czerwony';
    case OilColor.PINK:
      return 'Różowy';
    case OilColor.ORANGE:
      return 'Pomarańczowy';
    case OilColor.YELLOW:
      return 'Żółty';
    case OilColor.MILKY:
      return 'Mleczny';
    case OilColor.GOLD:
      return 'Złoty';
    case OilColor.WHITE:
      return 'Biały';
    case OilColor.BROWN:
      return 'Brązowy';
    case OilColor.BLACK:
      return 'Czarny';
    default:
      return 'Zielony';
  }
};

export const getVisualRatingStyles = (rating: VisualAssessmentRating) => {
  switch (rating) {
    case VisualAssessmentRating.EXCELLENT:
      return 'bg-green-700 text-white';
    case VisualAssessmentRating.GOOD:
      return 'bg-green-200 text-green-700';
    case VisualAssessmentRating.BELOW_AVERAGE:
      return 'bg-orangeBright text-white';
    case VisualAssessmentRating.POOR:
      return 'bg-red-800 text-white';
    default:
      return 'bg-green-700 text-white';
  }
};

export const getVisualRatingText = (rating: VisualAssessmentRating) => {
  switch (rating) {
    case VisualAssessmentRating.EXCELLENT:
      return 'Dobra';
    case VisualAssessmentRating.GOOD:
      return 'Adekwatna do przebiegu';
    case VisualAssessmentRating.BELOW_AVERAGE:
      return 'Zła';
    case VisualAssessmentRating.POOR:
      return 'Bardzo zła';
    default:
      return 'Dobra';
  }
};

export const getInspectionSummaryBackgroundColor = (
  rating: number | null,
): string => {
  if (rating === null) {
    return APP_COLORS.green[200];
  }

  const verdict = getVerdict(rating);

  switch (verdict) {
    case Verdict.POSITIVE:
      return APP_COLORS.green[200];
    case Verdict.NEUTRAL:
      return APP_COLORS.yellow;
    case Verdict.NEGATIVE:
      return APP_COLORS.red[200];
    default:
      return APP_COLORS.green[200];
  }
};
